import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { 
  Plus, 
  Trash2, 
  Edit, 
  CheckCircle, 
  XCircle,
  Smartphone,
  Shield,
  Zap,
  RefreshCw,
  Save,
  AlertCircle,
  Clock,
  BarChart3,
  Wifi,
  MessageSquare,
  Key
} from 'lucide-react';

import { Client } from '@/types/client';
import { apiService } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import AtomosChatApiGuide from './AtomosChatApiGuide';
import WebhooksTab from './WebhooksTab';
import MetaActivationWizard from './MetaActivationWizard';
import EmailRemetenteSection from './EmailRemetenteSection';

interface WhatsappConnection {
  id?: number;
  client_id: number;
  connection_type: 'oficial' | 'extra_oficial';
  name: string;
  is_active: boolean;
  
  // Monitoramento
  is_online?: boolean;
  connection_status?: 'connected' | 'disconnected' | 'error' | 'unknown';
  last_checked_at?: string;
  
  // Extra Oficial
  chatconnect_account_id?: string;
  
  // Oficial
  whatsapp_number?: string;
  phone_id?: string;
  business_account_id?: string;
  permanent_token?: string;
  chatconnect_oficial_inbox_id?: string;
  
  // Rate Limiting
  daily_limit?: number;
  sent_today?: number;
  min_interval_seconds?: number;
  max_interval_seconds?: number;
  delay_seg?: number;
  
  // Janela de envio
  window_start?: string;
  window_end?: string;
  window_start_formatted?: string | null;
  window_end_formatted?: string | null;
  
  created_at?: string;
  updated_at?: string;
}

/** Config e templates ÁtomosChat (Envios V2) */
interface AtomosChatConfigDto {
  id?: number;
  client_id?: number;
  account_token?: string;
  account_name?: string;
  channel_name?: string | null;
  channel_phone?: string | null;
  channel_id?: string | null;
  chatbot_id?: string | null;
  chatbot_name?: string | null;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}
interface AtomosChatTemplateDto {
  id: string;
  name: string;
  text?: string;
  status: string;
  categoryName?: string;
  channelId?: string;
}
interface ApiTabProps {
  client: Client;
}

const ApiTab: React.FC<ApiTabProps> = ({ client }) => {
  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState<WhatsappConnection[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<WhatsappConnection | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<Partial<WhatsappConnection>>({
    connection_type: 'oficial',
    is_active: true,
    window_start: '09:00',
    window_end: '18:00'
  });

  // Envios V2 (ÁtomosChat) — múltiplas conexões por cliente
  const [envioVersion, setEnvioVersion] = useState<'v1' | 'v2' | 'email'>('v1');
  const [atomosChatConfigs, setAtomosChatConfigs] = useState<AtomosChatConfigDto[]>([]);
  const [loadingAtomosChatConfig, setLoadingAtomosChatConfig] = useState(false);
  const [savingAtomosChatConfig, setSavingAtomosChatConfig] = useState(false);
  const [editingConfigId, setEditingConfigId] = useState<number | null>(null);
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [deletingConfigId, setDeletingConfigId] = useState<number | null>(null);
  const [atomosChatConfigForm, setAtomosChatConfigForm] = useState({
    account_token: '',
    account_name: '',
    channel_name: '',
    channel_phone: '',
    channel_id: ''
  });
  const [atomosChatTemplates, setAtomosChatTemplates] = useState<AtomosChatTemplateDto[]>([]);
  const [loadingAtomosChatTemplates, setLoadingAtomosChatTemplates] = useState(false);
  const [templatesConfigId, setTemplatesConfigId] = useState<number | null>(null);
  const [showMetaActivationWizard, setShowMetaActivationWizard] = useState(false);
  const [atomosChatError, setAtomosChatError] = useState<string>('');

  useEffect(() => {
    if (client?.id) {
      loadConnections();
      loadAtomosChatConfigs();
    }
  }, [client]);

  const loadConnections = async () => {
    if (!client?.id) return;
    setLoading(true);
    const timeoutMs = 15000;
    const timeoutId = setTimeout(() => {
      setLoading(false);
      setConnections([]);
    }, timeoutMs);
    try {
      const response = await apiService.get(`/whatsapp/client/${client.id}`);
      const connectionsData = Array.isArray(response) ? response : (response.data || response.connections || []);
      setConnections(connectionsData);
      console.log('✅ [ApiTab] Conexões carregadas:', connectionsData.length);
    } catch (error) {
      console.error('❌ [ApiTab] Erro ao carregar conexões:', error);
      setConnections([]);
      toast({
        title: 'Erro',
        description: 'Falha ao carregar conexões',
        variant: 'destructive'
      });
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  const resetAtomosForm = () => {
    setAtomosChatConfigForm({ account_token: '', account_name: '', channel_name: '', channel_phone: '', channel_id: '' });
  };

  const loadAtomosChatConfigs = async () => {
    if (!client?.id) return;
    try {
      setLoadingAtomosChatConfig(true);
      const [configsRes, versionRes] = await Promise.all([
        apiService.get(`/atomos-chat/client/${client.id}/configs`) as Promise<{ success?: boolean; configs?: AtomosChatConfigDto[] }>,
        apiService.get(`/atomos-chat/client/${client.id}/envio-version`) as Promise<{ success?: boolean; envio_version?: 'v1' | 'v2' | 'email' }>,
      ]);
      const configs = configsRes.configs ?? [];
      setAtomosChatConfigs(configs);
      setEnvioVersion(versionRes.envio_version ?? 'v1');
      setShowConfigForm((prev) => (editingConfigId != null ? prev : configs.length === 0));
      const firstActive = configs.find((c) => c.is_active !== false);
      if (firstActive && templatesConfigId == null) {
        setTemplatesConfigId(firstActive.id ?? null);
      }
    } catch (e) {
      console.error('[ApiTabClean] loadAtomosChatConfigs:', e);
      setAtomosChatConfigs([]);
      setEnvioVersion('v1');
    } finally {
      setLoadingAtomosChatConfig(false);
    }
  };

  const handleEnvioVersionChange = async (value: 'v1' | 'v2' | 'email') => {
    if (value === envioVersion) return;
    try {
      await apiService.put(`/atomos-chat/client/${client!.id}/envio-version`, { envio_version: value });
      setEnvioVersion(value);
      if (value === 'v2') {
        setShowConfigForm(atomosChatConfigs.length === 0);
      } else {
        // V1 ou Email: não usa o form/templates de V2
        setAtomosChatTemplates([]);
        setEditingConfigId(null);
        setShowConfigForm(false);
        resetAtomosForm();
      }
    } catch (e) {
      console.error('[ApiTabClean] handleEnvioVersionChange:', e);
      toast({ title: 'Erro ao alterar versão', description: 'Não foi possível salvar a preferência.', variant: 'destructive' });
    }
  };

  const startNewConfig = () => {
    setEditingConfigId(null);
    resetAtomosForm();
    setShowConfigForm(true);
    setAtomosChatError('');
  };

  const editAtomosChatConfig = (cfg: AtomosChatConfigDto) => {
    setEditingConfigId(cfg.id ?? null);
    setAtomosChatConfigForm({
      account_token: '',
      account_name: cfg.account_name ?? '',
      channel_name: cfg.channel_name ?? '',
      channel_phone: cfg.channel_phone ?? '',
      channel_id: cfg.channel_id ?? '',
    });
    setShowConfigForm(true);
    setAtomosChatError('');
  };

  const cancelConfigEdit = () => {
    setEditingConfigId(null);
    resetAtomosForm();
    setShowConfigForm(atomosChatConfigs.length === 0);
    setAtomosChatError('');
  };

  const deleteAtomosChatConfig = async (configId: number) => {
    if (!client?.id || !configId) return;
    if (!window.confirm('Remover esta conexão V2? Estratégias que apontavam para ela cairão no fallback (primeira ativa).')) return;
    try {
      setDeletingConfigId(configId);
      await apiService.delete(`/atomos-chat/client/${client.id}/configs/${configId}`);
      if (editingConfigId === configId) cancelConfigEdit();
      if (templatesConfigId === configId) setTemplatesConfigId(null);
      await loadAtomosChatConfigs();
      toast({ title: 'Conexão removida' });
    } catch (e: any) {
      console.error('[ApiTabClean] deleteAtomosChatConfig:', e);
      toast({ title: 'Erro ao remover', description: e?.message || 'Erro interno', variant: 'destructive' });
    } finally {
      setDeletingConfigId(null);
    }
  };

  const reactivateAtomosChatConfig = async (configId: number) => {
    if (!client?.id || !configId) return;
    try {
      setSavingAtomosChatConfig(true);
      await apiService.put(`/atomos-chat/client/${client.id}/configs/${configId}`, { is_active: true });
      await loadAtomosChatConfigs();
      toast({ title: 'Conexão reativada' });
    } catch (e: any) {
      console.error('[ApiTabClean] reactivateAtomosChatConfig:', e);
      toast({ title: 'Erro ao reativar', description: e?.message || 'Erro interno', variant: 'destructive' });
    } finally {
      setSavingAtomosChatConfig(false);
    }
  };

  const saveAtomosChatConfig = async () => {
    if (!client?.id) return;
    const isCreate = editingConfigId == null;
    if (isCreate && !atomosChatConfigForm.account_token.trim()) {
      setAtomosChatError('Informe o token da conta para criar a conexão.');
      return;
    }
    if (!atomosChatConfigForm.account_name.trim()) {
      setAtomosChatError('Informe o nome do cliente na plataforma.');
      return;
    }
    try {
      setSavingAtomosChatConfig(true);
      setAtomosChatError('');
      const payload: Record<string, string> = { account_name: atomosChatConfigForm.account_name.trim() };
      if (atomosChatConfigForm.account_token.trim()) payload.account_token = atomosChatConfigForm.account_token.trim();
      if (atomosChatConfigForm.channel_name?.trim()) payload.channel_name = atomosChatConfigForm.channel_name.trim();
      if (atomosChatConfigForm.channel_phone?.trim()) payload.channel_phone = atomosChatConfigForm.channel_phone.trim();
      if (atomosChatConfigForm.channel_id?.trim()) payload.channel_id = atomosChatConfigForm.channel_id.trim();
      if (isCreate) {
        await apiService.post(`/atomos-chat/client/${client.id}/configs`, payload);
      } else {
        await apiService.put(`/atomos-chat/client/${client.id}/configs/${editingConfigId}`, payload);
      }
      setEditingConfigId(null);
      resetAtomosForm();
      await loadAtomosChatConfigs();
      setShowConfigForm(false);
      toast({ title: isCreate ? 'Conexão criada' : 'Conexão atualizada', description: 'Configuração ÁtomosChat salva com sucesso.' });
    } catch (e: any) {
      console.error('[ApiTabClean] saveAtomosChatConfig:', e);
      const msg = e?.message ?? 'Erro ao salvar';
      setAtomosChatError(msg);
      toast({ title: 'Erro ao salvar', description: msg, variant: 'destructive' });
    } finally {
      setSavingAtomosChatConfig(false);
    }
  };

  const loadAtomosChatTemplates = async () => {
    if (!client?.id) return;
    try {
      setLoadingAtomosChatTemplates(true);
      const url = templatesConfigId
        ? `/atomos-chat/client/${client.id}/templates?config_id=${templatesConfigId}`
        : `/atomos-chat/client/${client.id}/templates`;
      const data = await apiService.get(url) as { success?: boolean; templates?: AtomosChatTemplateDto[]; count?: number };
      setAtomosChatTemplates(data.templates ?? []);
    } catch (e) {
      console.error('[ApiTabClean] loadAtomosChatTemplates:', e);
      setAtomosChatTemplates([]);
      toast({ title: 'Erro', description: 'Falha ao carregar templates', variant: 'destructive' });
    } finally {
      setLoadingAtomosChatTemplates(false);
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setIsEditing(false);
    setSelectedConnection(null);
    setFormData({
      client_id: client.id,
      connection_type: 'oficial',
      name: '',
      is_active: true,
      daily_limit: 1000,
      delay_seg: 1,
      window_start: '09:00',
      window_end: '18:00'
    });
  };

  const handleEdit = (connection: WhatsappConnection) => {
    setIsEditing(true);
    setIsCreating(false);
    setSelectedConnection(connection);
    setFormData({ ...connection });
  };

  const handleCancel = () => {
    setIsCreating(false);
    setIsEditing(false);
    setSelectedConnection(null);
    setFormData({
      connection_type: 'oficial',
      is_active: true
    });
  };

  const handleSave = async () => {
    try {
      if (!formData.name?.trim()) {
        toast({
          title: 'Erro',
          description: 'Nome da conexão é obrigatório',
          variant: 'destructive'
        });
        return;
      }

      let connectionId: number | null = null;
      if (isCreating) {
        const res = await apiService.post('/whatsapp/create', {
          ...formData,
          client_id: client.id
        }) as { connection?: { id?: number }; data?: { id?: number } };
        connectionId = res?.connection?.id ?? res?.data?.id ?? null;
        toast({
          title: 'Sucesso',
          description: 'Conexão criada com sucesso'
        });
      } else if (isEditing && selectedConnection) {
        connectionId = selectedConnection.id ?? null;
        await apiService.put(`/whatsapp/${selectedConnection.id}`, formData);
        toast({
          title: 'Sucesso',
          description: 'Conexão atualizada com sucesso'
        });
      }

      // Salvar janela de envio (horário) se preenchida
      const windowStart = formData.window_start || (isEditing && selectedConnection ? (selectedConnection.window_start_formatted ?? selectedConnection.window_start) : undefined);
      const windowEnd = formData.window_end || (isEditing && selectedConnection ? (selectedConnection.window_end_formatted ?? selectedConnection.window_end) : undefined);
      if (connectionId && windowStart && windowEnd) {
        try {
          await apiService.put(`/whatsapp/${connectionId}/window`, { window_start: windowStart, window_end: windowEnd });
        } catch (err) {
          console.error('[ApiTabClean] Erro ao salvar janela de envio:', err);
          toast({ title: 'Aviso', description: 'Conexão salva, mas falha ao salvar horário de envio.', variant: 'destructive' });
        }
      }

      handleCancel();
      loadConnections();
    } catch (error: any) {
      console.error('❌ [ApiTab] Erro ao salvar:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.error || 'Falha ao salvar conexão',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Deseja realmente excluir esta conexão?')) return;

    try {
      await apiService.delete(`/whatsapp/${id}`);
      toast({
        title: 'Sucesso',
        description: 'Conexão excluída com sucesso'
      });
      loadConnections();
    } catch (error) {
      console.error('❌ [ApiTab] Erro ao excluir:', error);
      toast({
        title: 'Erro',
        description: 'Falha ao excluir conexão',
        variant: 'destructive'
      });
    }
  };

  const getStatusBadge = (connection: WhatsappConnection) => {
    if (!connection.is_active) {
      return <Badge variant="secondary">Inativa</Badge>;
    }
    
    if (connection.connection_status === 'connected' || connection.is_online) {
      return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Online</Badge>;
    }
    
    if (connection.connection_status === 'disconnected') {
      return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Offline</Badge>;
    }
    
    return <Badge variant="outline">Desconhecido</Badge>;
  };

  const getTypeBadge = (type: string) => {
    if (type === 'oficial') {
      return <Badge variant="default" className="bg-blue-600"><Shield className="w-3 h-3 mr-1" />Meta API Oficial</Badge>;
    }
    return <Badge variant="default" className="bg-purple-600"><Zap className="w-3 h-3 mr-1" />API Extra Oficial</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header + Modo de envio */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">API de Envio</h2>
          <p className="text-sm text-gray-600 mt-1">
            Gerencie as APIs de envio de mensagens do cliente
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Label className="text-sm text-gray-600 whitespace-nowrap">Modo de envio:</Label>
          <Select value={envioVersion} onValueChange={(v) => handleEnvioVersionChange(v as 'v1' | 'v2' | 'email')} disabled={loadingAtomosChatConfig}>
            <SelectTrigger className="w-[220px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="v1">Envios - V1 (WhatsApp)</SelectItem>
              <SelectItem value="v2">Envios - V2 (ÁtomosChat)</SelectItem>
              <SelectItem value="email">Envios - Email</SelectItem>
            </SelectContent>
          </Select>
          {envioVersion === 'v1' && (
            <Button onClick={handleCreate} disabled={isCreating || isEditing}>
              <Plus className="w-4 h-4 mr-2" />
              Nova Conexão
            </Button>
          )}
        </div>
      </div>

      {/* Modo Email: configuração de remetente do cliente (global vs. próprio) */}
      {envioVersion === 'email' && (
        <EmailRemetenteSection client={client} />
      )}

      {/* Ativar API Meta — sempre visível quando V1 está selecionado */}
      {envioVersion === 'v1' && (
        <Card className="border-2 border-purple-200 bg-purple-50/50">
          <CardContent className="py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <Key className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="font-semibold text-purple-900">Ativar API Meta</div>
                <div className="text-sm text-purple-700">
                  Configure 2FA da conta Meta e capture o phone_id automaticamente
                </div>
              </div>
            </div>
            <Button
              onClick={() => setShowMetaActivationWizard(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Key className="w-4 h-4 mr-2" />
              Iniciar wizard
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Envios V2 (ÁtomosChat): Form de cadastro/edição visível somente em V2 */}
      {envioVersion === 'v2' && showConfigForm && (
        <AtomosChatApiGuide
          form={atomosChatConfigForm}
          onFormChange={(field, value) => setAtomosChatConfigForm(prev => ({ ...prev, [field]: value }))}
          onSave={saveAtomosChatConfig}
          saving={savingAtomosChatConfig}
          error={atomosChatError}
          mode={editingConfigId == null ? 'create' : 'edit'}
          onCancel={atomosChatConfigs.length > 0 ? cancelConfigEdit : undefined}
          clientId={client.id}
          editingConfigId={editingConfigId}
        />
      )}

      {/* Lista de conexões V2 cadastradas — sempre que houver pelo menos uma OU quando o cliente está em V2 */}
      {envioVersion !== 'email' && (envioVersion === 'v2' || atomosChatConfigs.length > 0) && (
        <Card className="border-2 border-teal-200 bg-teal-50/50">
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <div>
                <CardTitle className="flex items-center gap-2 text-teal-800">
                  <Smartphone className="w-5 h-5" />
                  Conexões V2 cadastradas (ÁtomosChat)
                </CardTitle>
                <CardDescription className="text-teal-600">
                  Contas/canais ÁtomosChat deste cliente. Cada estratégia V2 escolhe qual conexão envia.
                </CardDescription>
              </div>
              {envioVersion === 'v2' && !showConfigForm && (
                <Button onClick={startNewConfig} className="bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar conexão
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {envioVersion === 'v1' && atomosChatConfigs.length > 0 && (
              <div className="mb-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                Estas conexões só são usadas quando o modo de envio do cliente é <strong>V2</strong>. Mude o seletor acima para "Envios - V2" para ativá-las.
              </div>
            )}
            {loadingAtomosChatConfig ? (
              <div className="text-sm text-gray-600">Carregando conexões...</div>
            ) : atomosChatConfigs.length === 0 ? (
              <div className="text-sm text-gray-600">
                Nenhuma conexão V2 cadastrada. Use o formulário acima para criar a primeira.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-teal-200 text-left text-teal-800">
                      <th className="py-2 pr-3 font-semibold">Status</th>
                      <th className="py-2 pr-3 font-semibold">Conta</th>
                      <th className="py-2 pr-3 font-semibold">Canal</th>
                      <th className="py-2 pr-3 font-semibold">Telefone</th>
                      <th className="py-2 pr-3 font-semibold">Channel ID</th>
                      <th className="py-2 pr-3 font-semibold">Token</th>
                      <th className="py-2 pr-3 font-semibold">Criada em</th>
                      <th className="py-2 pr-3 font-semibold text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {atomosChatConfigs.map((cfg) => (
                      <tr key={cfg.id} className={`border-b border-teal-100 ${editingConfigId === cfg.id ? 'bg-teal-100/40' : ''} ${cfg.is_active === false ? 'opacity-60' : ''}`}>
                        <td className="py-2 pr-3">
                          {cfg.is_active === false ? (
                            <Badge variant="secondary" className="text-xs">Inativa</Badge>
                          ) : (
                            <Badge variant="default" className="text-xs bg-teal-600">Ativa</Badge>
                          )}
                        </td>
                        <td className="py-2 pr-3 font-medium text-gray-900">{cfg.account_name || '-'}</td>
                        <td className="py-2 pr-3 text-gray-700">{cfg.channel_name || '-'}</td>
                        <td className="py-2 pr-3 text-gray-700">{cfg.channel_phone || '-'}</td>
                        <td className="py-2 pr-3 text-gray-700 font-mono text-xs">{cfg.channel_id || '-'}</td>
                        <td className="py-2 pr-3 text-gray-500 font-mono text-xs">••••••••</td>
                        <td className="py-2 pr-3 text-gray-600">
                          {cfg.created_at ? new Date(cfg.created_at).toLocaleDateString('pt-BR') : '-'}
                        </td>
                        <td className="py-2 pr-0 text-right">
                          <div className="flex justify-end gap-1">
                            {cfg.is_active === false ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-teal-700 hover:text-teal-800"
                                onClick={() => cfg.id && reactivateAtomosChatConfig(cfg.id)}
                                disabled={savingAtomosChatConfig || envioVersion !== 'v2'}
                                title={envioVersion === 'v2' ? 'Reativar conexão' : 'Mude para V2 para reativar'}
                              >
                                Reativar
                              </Button>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => editAtomosChatConfig(cfg)}
                                  disabled={savingAtomosChatConfig || deletingConfigId === cfg.id || envioVersion !== 'v2'}
                                  title={envioVersion === 'v2' ? 'Editar conexão' : 'Mude para V2 para editar'}
                                >
                                  <Edit className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => cfg.id && deleteAtomosChatConfig(cfg.id)}
                                  disabled={deletingConfigId === cfg.id || envioVersion !== 'v2'}
                                  title={envioVersion === 'v2' ? 'Remover conexão' : 'Mude para V2 para remover'}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {envioVersion === 'v2' && (
        <>
          <Card className="border-2 border-teal-200 bg-teal-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-teal-800">
                <MessageSquare className="w-5 h-5" />
                Templates ÁtomosChat
              </CardTitle>
              <CardDescription className="text-teal-600">
                Carregue os templates da API ÁtomosChat (Type=ATTENDANCE) para uma conexão específica
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                <div className="flex-1 min-w-[240px]">
                  <Label className="text-sm">Conexão</Label>
                  <Select
                    value={templatesConfigId ? String(templatesConfigId) : ''}
                    onValueChange={(v) => setTemplatesConfigId(v ? Number(v) : null)}
                    disabled={atomosChatConfigs.filter(c => c.is_active !== false).length === 0}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Selecione uma conexão" />
                    </SelectTrigger>
                    <SelectContent>
                      {atomosChatConfigs.filter(c => c.is_active !== false).map((cfg) => (
                        <SelectItem key={cfg.id} value={String(cfg.id)}>
                          {cfg.account_name}{cfg.channel_phone ? ` — ${cfg.channel_phone}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={loadAtomosChatTemplates}
                  disabled={loadingAtomosChatTemplates || atomosChatConfigs.length === 0 || !templatesConfigId}
                >
                  {loadingAtomosChatTemplates ? 'Carregando...' : 'Carregar templates'}
                </Button>
              </div>
              {atomosChatConfigs.filter(c => c.is_active !== false).length === 0 && (
                <span className="text-sm text-amber-600 mt-2 block">
                  Cadastre (ou reative) uma conexão V2 antes de carregar os templates.
                </span>
              )}
              {atomosChatTemplates.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Templates encontrados ({atomosChatTemplates.length})</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {atomosChatTemplates.slice(0, 6).map((template) => (
                      <div key={template.id} className="border rounded-lg p-3 bg-gray-50">
                        <div className="font-medium text-sm">{template.name}</div>
                        <div className="text-xs text-gray-600 mt-1">{template.status} • {template.categoryName ?? '-'}</div>
                      </div>
                    ))}
                    {atomosChatTemplates.length > 6 && (
                      <div className="border rounded-lg p-3 bg-gray-100 flex items-center justify-center">
                        <span className="text-sm text-gray-600">+{atomosChatTemplates.length - 6} mais...</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Webhook de Boleto (Automação V2) */}
          <WebhooksTab client={client} />
        </>
      )}

      {/* Conteúdo V1: Form e Lista de Conexões WhatsApp */}
      {envioVersion === 'v1' && (
        <>

      {/* Form de Criação/Edição */}
      {(isCreating || isEditing) && (
        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle>{isCreating ? 'Nova Conexão' : 'Editar Conexão'}</CardTitle>
            <CardDescription>
              Preencha os dados da conexão WhatsApp
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Tipo e Nome */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Tipo de API *</Label>
                <Select
                  value={formData.connection_type}
                  onValueChange={(value: 'oficial' | 'extra_oficial') =>
                    setFormData(prev => ({ ...prev, connection_type: value }))
                  }
                  disabled={isEditing}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="oficial">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-blue-600" />
                        Meta API Oficial
                      </div>
                    </SelectItem>
                    <SelectItem value="extra_oficial">
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-purple-600" />
                        API Extra Oficial (Quepasa)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Nome da Conexão *</Label>
                <Input
                  value={formData.name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: WhatsApp Principal"
                />
              </div>
            </div>

            {/* Campos Específicos por Tipo */}
            {formData.connection_type === 'oficial' ? (
              <div className="space-y-4 p-4 bg-blue-100 rounded-lg">
                <h3 className="font-semibold flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Configurações Meta API Oficial
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Número WhatsApp</Label>
                    <Input
                      value={formData.whatsapp_number || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, whatsapp_number: e.target.value }))}
                      placeholder="5511999999999"
                    />
                  </div>
                  <div>
                    <Label>Phone ID</Label>
                    <Input
                      value={formData.phone_id || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone_id: e.target.value }))}
                      placeholder="123456789"
                    />
                  </div>
                  <div>
                    <Label>Business Account ID</Label>
                    <Input
                      value={formData.business_account_id || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, business_account_id: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Token Permanente</Label>
                    <Input
                      type="password"
                      value={formData.permanent_token || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, permanent_token: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>ID da Conta ChatConnect</Label>
                    <Input
                      value={formData.chatconnect_account_id || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, chatconnect_account_id: e.target.value }))}
                      placeholder="ID da conta no ChatConnect"
                    />
                  </div>
                  <div>
                    <Label>ID Inbox Oficial ChatConnect</Label>
                    <Input
                      value={formData.chatconnect_oficial_inbox_id || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, chatconnect_oficial_inbox_id: e.target.value }))}
                      placeholder="ID do inbox oficial"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4 p-4 bg-purple-100 rounded-lg">
                <h3 className="font-semibold flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Configurações API Extra Oficial (Quepasa)
                </h3>
                <div>
                  <Label>ID da Conta ChatConnect</Label>
                  <Input
                    value={formData.chatconnect_account_id || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, chatconnect_account_id: e.target.value }))}
                    placeholder="abc123xyz"
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    Este ID é usado na URL da API Quepasa
                  </p>
                </div>
              </div>
            )}

            {/* Limites e Rate Limiting */}
            <div className="space-y-4 p-4 bg-gray-100 rounded-lg">
              <h3 className="font-semibold flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Limites e Controle de Envio
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Limite Diário</Label>
                  <Input
                    type="number"
                    value={formData.daily_limit || 1000}
                    onChange={(e) => setFormData(prev => ({ ...prev, daily_limit: parseInt(e.target.value) }))}
                  />
                </div>
                <div>
                  <Label>Delay (segundos)</Label>
                  <Input
                    type="number"
                    value={formData.delay_seg || 1}
                    onChange={(e) => setFormData(prev => ({ ...prev, delay_seg: parseInt(e.target.value) }))}
                  />
                </div>
                <div className="flex items-end">
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                    />
                    <Label>Conexão Ativa</Label>
                  </div>
                </div>
              </div>
            </div>

            {/* Horário de envio (janela) */}
            <div className="space-y-4 p-4 bg-slate-100 rounded-lg border border-slate-200">
              <h3 className="font-semibold flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Horário de envio
              </h3>
              <p className="text-sm text-gray-600">
                Período do dia em que mensagens podem ser enviadas por esta conexão.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Início</Label>
                  <Input
                    type="time"
                    value={formData.window_start ?? (selectedConnection?.window_start_formatted ?? selectedConnection?.window_start ?? '09:00')}
                    onChange={(e) => setFormData(prev => ({ ...prev, window_start: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Fim</Label>
                  <Input
                    type="time"
                    value={formData.window_end ?? (selectedConnection?.window_end_formatted ?? selectedConnection?.window_end ?? '18:00')}
                    onChange={(e) => setFormData(prev => ({ ...prev, window_end: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Ações */}
            <div className="flex items-center gap-3 pt-4 border-t">
              <Button onClick={handleSave}>
                <Save className="w-4 h-4 mr-2" />
                Salvar
              </Button>
              <Button variant="outline" onClick={handleCancel}>
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de Conexões */}
      {loading && connections.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <div className="flex flex-col items-center gap-3">
              <span className="inline-block w-8 h-8 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
              <p className="text-sm text-gray-600">Carregando conexões...</p>
            </div>
          </CardContent>
        </Card>
      ) : connections.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Smartphone className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Nenhuma conexão cadastrada
            </h3>
            <p className="text-gray-600 mb-6">
              Crie sua primeira conexão WhatsApp para começar a enviar mensagens
            </p>
            <Button onClick={handleCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Criar Primeira Conexão
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {connections.map((connection) => (
            <Card key={connection.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Smartphone className="w-5 h-5 text-gray-600" />
                      <CardTitle className="text-lg">{connection.name}</CardTitle>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {getTypeBadge(connection.connection_type)}
                      {getStatusBadge(connection)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(connection)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(connection.id!)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Informações Principais */}
                <div className="space-y-2 text-sm">
                  {connection.connection_type === 'oficial' ? (
                    <>
                      {connection.whatsapp_number && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600">Número:</span>
                          <span className="font-mono">{connection.whatsapp_number}</span>
                        </div>
                      )}
                      {connection.phone_id && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600">Phone ID:</span>
                          <span className="font-mono text-xs">{connection.phone_id}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {connection.chatconnect_account_id && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600">Account ID:</span>
                          <span className="font-mono text-xs">{connection.chatconnect_account_id}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <Separator />

                {/* Métricas */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-gray-600 mb-1">Limite Diário</div>
                    <div className="font-semibold">{connection.daily_limit || 0}</div>
                  </div>
                  <div>
                    <div className="text-gray-600 mb-1">Enviados Hoje</div>
                    <div className="font-semibold text-blue-600">{connection.sent_today || 0}</div>
                  </div>
                  <div>
                    <div className="text-gray-600 mb-1">Delay</div>
                    <div className="font-semibold flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {connection.delay_seg || 1}s
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-gray-600 mb-1">Horário de envio</div>
                    <div className="font-semibold flex items-center gap-1 text-sm">
                      <Clock className="w-3 h-3" />
                      {(connection.window_start_formatted ?? connection.window_start ?? '00:00')} - {(connection.window_end_formatted ?? connection.window_end ?? '23:59')}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-600 mb-1">Status</div>
                    <div className="font-semibold">
                      {connection.is_active ? (
                        <span className="text-green-600">Ativa</span>
                      ) : (
                        <span className="text-gray-400">Inativa</span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Informações e Avisos */}
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>Importante:</strong> Mantenha suas credenciais seguras. Para API Extra Oficial (Quepasa), 
          configure o fluxo de mensagens na aba "Fluxos de Envio".
        </AlertDescription>
      </Alert>
        </>
      )}

      <MetaActivationWizard
        isOpen={showMetaActivationWizard}
        onClose={() => setShowMetaActivationWizard(false)}
      />
    </div>
  );
};

export default ApiTab;
